'use strict'

import 'intersection-observer';

import { getCalendarData, getPerformances } from '../services/inventory';
import { loadComponent } from './utils/load-component';
import { format } from 'date-fns';

// Filter group IDs (also used as URL params) mapped to the keys on perf.Filters
const filterKeyMap = {
    type: 'Type',
    venue: 'Venue',
    keywords: 'Keywords',
};

loadComponent(
    'calendar-list',
    {
        props: [
            'calendarItemFocusedClass',
        ],
        data: function () {
            return {
                ViewModel: {
                    Loading: true,
                    Years: [],
                },
                selectedLocation: '',
                filters: {},
                totalUnfilteredCount: 0,
            };
        },

        beforeMount: function () {
            this.$root.$on('DatePicker.selectedDate', this.dateSelected);
            this.$root.$on('CalendarFilters.filtersUpdated', this.filtersChanged);
            this.$root.$on('CalendarFilters.locationUpdated', this.locationChanged);

            getCalendarData(this.selectedLocation)
                .then(result => {
                    this.ViewModel.Years = result.yearData;
                    this.totalUnfilteredCount = result.totalCount;
                    return this.updateFilters();
                })
                .then(() => {
                    return this.updateFiltersFromUrl();
                })
                .finally(() => {
                    this.ViewModel.Loading = false;

                    window.addEventListener('popstate', () => {
                        this.updateFiltersFromUrl();
                    });
                });
        },

        methods: {
            performanceClicked: function (perf) {
                this.$root.$emit('CalendarPopup.scrollToPerformance', perf);
            },

            perfDateId: function (perf) {
                return `date-${format(perf.PerformanceDate, 'yyyy-MM-dd')}`;
            },

            updateFilters: function () {
                return getPerformances().then(perfs => {
                    let filters = [
                        {
                            ID: 'type',
                            Name: 'Type',
                            Options: [],
                        },
                        {
                            ID: 'venue',
                            Name: 'Venue',
                            Options: [],
                        },
                        {
                            ID: 'keywords',
                            Name: 'Keywords',
                            Options: [],
                        },
                    ];

                    perfs.reduce((filters, perf) => {
                        // Type
                        const typeFilter = filters[0];
                        if (perf.Filters.Type) {
                            perf.Filters.Type.forEach(type => {
                                const opt = typeFilter.Options.find(item => item.ID === type);
                                if (!opt) {
                                    typeFilter.Options.push({
                                        Value: type,
                                        ID: type,
                                        Label: type,
                                    });
                                }
                            });
                        }

                        // Venue
                        const venueFilter = filters[1];
                        if (perf.Filters.Venue) {
                            perf.Filters.Venue.forEach(venue => {
                                const opt = venueFilter.Options.find(item => item.ID === venue);
                                if (!opt) {
                                    venueFilter.Options.push({
                                        Value: venue,
                                        ID: venue,
                                        Label: venue,
                                    });
                                }
                            });
                        }

                        // Keywords
                        const keywordsFilter = filters[2];
                        if (perf.Filters.Keywords) {
                            Object.keys(perf.Filters.Keywords).forEach(key => {
                                const keyword = perf.Filters.Keywords[key];
                                const opt = keywordsFilter.Options.find(item => item.ID === keyword);
                                if (!opt) {
                                    keywordsFilter.Options.push({
                                        Value: keyword,
                                        ID: keyword,
                                        Label: keyword,
                                        Order: parseInt(key, 10),
                                    });
                                }
                            });
                        }

                        return filters;
                    }, filters);

                    filters[2].Options.sort((a, b) => a.Order - b.Order);

                    this.$root.$emit('CalendarFilter.updateFilters', filters);
                })
            },

            updateFiltersFromUrl: function () {
                const searchParams = new URLSearchParams(window.location.search);

                const filters = {};

                searchParams.forEach((value, key) => {
                    if (key === 'location') {
                        //Location referring to 'Lincoln Center' or 'On Tour'
                        //handled separately from the other filters
                        return;
                    }

                    if (key in filters === false) {
                        filters[key] = [];
                    }

                    filters[key].push(value);
                });

                this.$root.$emit('CalendarFilter.updateAppliedFilters', filters);
                return this.filtersChanged(filters, false);
            },

            filtersChanged: function (filters, updateUrl = true) {
                if (updateUrl) {
                    const searchParams = new URLSearchParams();

                    for (const filterKey of Object.keys(filters)) {
                        for (const filterValue of filters[filterKey]) {
                            searchParams.append(filterKey, filterValue);
                        }
                    }

                    const url = new URL(window.location);

                    url.search = searchParams.toString();

                    window.history.pushState(null, '', url.toString());
                }

                this.filters = filters;
                return this.updatePerformances()
            },

            updatePerformances(){
                return getCalendarData(this.selectedLocation)
                    .then(result => {
                        this.totalUnfilteredCount = result.totalCount;
                        this.ViewModel.Years = result.yearData.reduce((carry, year) => {
                            const months = year.Months.reduce((carry, month) => {
                                const perfs = month.Performances.filter(perf => {
                                    return Object.keys(this.filters).every(filterId => {
                                        // Not in the filter so just include
                                        if (!this.filters[filterId].length) {
                                            return true;
                                        }

                                        const filterKey = filterKeyMap[filterId] || filterId;

                                        if (!perf.Filters[filterKey]) {
                                            return false;
                                        }

                                        // Check if the performance has ALL selected filters for this category
                                        return this.filters[filterId].every(selectedFilterId => {
                                            if (filterKey === 'Keywords') {
                                                const keywordValues = Object.values(perf.Filters[filterKey]);
                                                return keywordValues.some(
                                                    filterItem => filterItem === selectedFilterId
                                                );
                                            } else {
                                                return perf.Filters[filterKey].some(
                                                    filterItem => filterItem === selectedFilterId
                                                );
                                            }
                                        });
                                    });
                                });

                                if (perfs.length) {
                                    carry.push({
                                        Name: month.Name,
                                        Performances: perfs,
                                    })
                                }
                                return carry;
                            }, []);
                            if (months.length) {
                                carry.push({
                                    Name: year.Name,
                                    Months: months,
                                })
                            }
                            return carry;
                        }, []);

                        // Reduces the Performances into a single level array and passes to CalendarPopup
                        this.$root.$emit('CalendarPopup.updatePerformances', this.ViewModel.Years.reduce((carry, year) => {
                            return year.Months.reduce((carry, month) => {
                                return carry.concat(month.Performances);
                            }, carry);
                        }, []));
                        this.updateCalendarDates();
                    })
            },

            clearFilters: function() {
                this.$root.$emit('CalendarFilters.clearFilters');
                this.filters = {};
                this.filtersChanged({}, true);
            },

            locationChanged: function(newLocation) {
                this.selectedLocation = newLocation;
                this.updatePerformances();
            },

            updateCalendarDates: function() {
                const newDates = this.ViewModel.Years.reduce((dates, year) => year.Months.reduce((dates, month) => month.Performances.reduce((dates, perf) => {
                        dates.push(format(perf.PerformanceDate, 'yyyy-MM-dd'));
                        return dates;
                    }, dates), dates), []);

                this.$root.$emit(
                    'DatePicker.updateCalendarDates',
                    newDates
                );
            },

            dateSelected: function (dateString) {
                // eslint-disable-next-line
                const observer = new IntersectionObserver(entries => {
                    const [firstEl] = entries;

                    if (firstEl.isIntersecting) {
                        setTimeout(() => {
                            els.forEach(el => el.classList.remove(this.calendarItemFocusedClass));
                        }, 250);
                        observer.disconnect();
                    }
                });
                const els = document.querySelectorAll(`#date-${dateString}`);
                observer.observe(els.item(0));
                els.item(0).scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                els.forEach(el => {
                    el.classList.add(this.calendarItemFocusedClass);
                });
            },
        },
    }
)
